import {Query} from 'react-apollo';
import {GetAllProductInstructions} from '@services/ResourcesService/query.graphql';
import {AccordionItem, Processing, Search} from '@ultradent/components';
import {client} from '@core/GraphQLProvider';
import sortBy from 'ramda/src/sortBy';
import prop from 'ramda/src/prop';
import Provider from 'preact-context-provider';

const LBL_SEARCH = upi.i18n.getTextFromKey( 'lbl-search', 'Search' );
const LBL_PRODUCT_NAME = upi.i18n.getTextFromKey( 'lbl-productName', 'Product Name' );
const LBL_PRODUCTS = upi.i18n.getTextFromKey( 'lbl-products', 'Products' );
const LBL_PRODUCT_INSTRUCTIONS = upi.i18n.getTextFromKey( 'lbl-productInstructions', 'Product Instructions' );
const LBL_NO_RESULTS = upi.i18n.getTextFromKey( 'lbl-noResults', 'No results' );

const ERROR_HEADING = upi.i18n.getTextFromKey( 'lbl-errorsListHeading',
    'Sorry, there was problem getting this list.' );
const LIST_EMPTY_LABEL = upi.i18n.getTextFromKey( 'lbl-ListEmptyHeadingGeneric',
    'Sorry, we were not able to find any items for this region.' );
const LIST_EMPTY_PHRASE = upi.i18n.getTextFromKey( 'lbl-ListEmptyPhraseGeneric',
        'Please try changing your selection or for more information contact our customer service at %PHONE_TOLL_FREE%' )
    .replace( '%PHONE_TOLL_FREE%', upi.conf.phoneTollFree );

const ListEmpty = () => (
    <div className="mb-12 subheading-sm leading-normal">
        <h2 className="text-lg font-normal">{LIST_EMPTY_LABEL}</h2>
        <p className="mt-2 max-w-lg">{LIST_EMPTY_PHRASE}</p>
    </div>
);

const ListError = () => (
    <div className="mb-12 subheading-sm leading-normal">
        <h2 className="text-lg font-normal color-error">{ERROR_HEADING}</h2>
    </div>
);

const Loading = () =>
    <div className="flex w-full justify-center h-64">
        <Processing/>
    </div>;

const NoResults = () =>
    <div className="py-3 border-t border-solid border-grey-lighter">
        <span className="text-title italic ml-10">{LBL_NO_RESULTS}</span>
    </div>;

function reduceToGroups ( list ) {
    return list.reduce( ( acc, next ) => {

        if ( !next.groups || next.groups.length <= 0 ) {
            next.groups = null;
            acc.push( {group: null, items: [next]} );
            return acc
        }
        // group by group
        next.groups.forEach( groupName => {
            let group = acc.find( item => item.group === groupName );

            if ( !group ) {
                group = {group: groupName, items: []};
                acc.push( group );
            }

            group.items.push( next );
        } );

        return acc;
    }, [] );
}

const GroupedRow = ( {line, isActive} ) =>
    <AccordionItem
        isActive={isActive}
        headingClassName="text-title py-2"
        className="border-t border-grey-lighter border-solid"
        label={line.group}>
        <GroupDetail items={line.items}/>
    </AccordionItem>

const LinkRow = ( {line} ) =>
    (line.document != null)
    ? <div className="border-t border-grey-lighter border-solid">
            <div className="md:flex py-3">
            <span className="text-title">
                <a href={line.document.source}
                   target="_blank"
                   className="inline-block ml-10 mr-2 link-3">{line.title}</a>
            </span>
            </div>
        </div>
    : null;

const GroupDetail = ( {items} ) => {
    const itemsSorted = sortBy( prop( 'title' ) )( items );

    return (
        <div className="text-body-sm font-semibold ml-10 mr-2 mb-4">
            {itemsSorted.map( item =>
                (item.document != null)
                ? <a className="block font-bold link-3" href={item.document.source} target="_blank">{item.title}</a>
                : null
            )}
        </div>
    );
}

const List = ( {list = [], isSearching, isLoading} ) => {
    if ( isLoading ) {
        return <Loading/>;
    }

    if ( !list.length ) {
        return <ListEmpty/>
    }

    const groupedList = sortBy( prop( 'group' ) )( reduceToGroups( list ) );

    return groupedList.map( line => (
        ((isSearching && line.group) || line.items.length > 1)
        ? <GroupedRow line={line} isActive={isSearching}/>
        : <LinkRow line={line.items[0]}/>
    ) )
};

const ProductInstructionsSearch = ( {list}, context ) =>
    <Search
        initialQuery={''}
        queryGroup={[{
            list: list,
            queryProps: ['group', 'title']
        }]}>
        {( searchProp ) =>
            <div className="mb-6 border-b border-grey-lighter border-solid">
                <fieldset className="md:flex">
                    <div className="input-group md:w-1/3">
                        <label className="subheading-base" htmlFor="ifusearch">{LBL_SEARCH} {LBL_PRODUCTS}</label>
                        <Search.Input id="ifusearch" placeholder={LBL_PRODUCT_NAME}/>
                    </div>
                </fieldset>
                <div className="hidden md:flex py-2">
                    <h3 className="headline-xs">{LBL_PRODUCT_INSTRUCTIONS}</h3>
                </div>
                {(!searchProp.hasResults && !searchProp.isActive)

                 ? <List list={list} isLoading={context.loading} isSearching={false}/>
                 : <Search.Results>
                     {( resultGroups ) => {
                         if ( !resultGroups[0] ) {
                             return <NoResults/>
                         }

                         return (
                             <List list={resultGroups[0].props.results}
                                   isLoading={context.loading}
                                   isSearching={true}/>
                         );
                     }}
                 </Search.Results>
                }
            </div>
        }
    </Search>;

export const ProductInstructions = () =>
    <Query
        client={client}
        query={GetAllProductInstructions}>
        {( {data, error, loading} ) => {

            if ( error || !data.allIfus ) {
                return <ListError/>;
            }

            return <Provider loading={loading}>
                <ProductInstructionsSearch list={data.allIfus}/>
            </Provider>
        }}
    </Query>;
